(() => {
  'use strict';

  const focusableSelector = [
    'summary',
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable="true"]',
    'object',
    'iframe'
  ].join(',');

  let trappedContainer = null;
  let trapKeydownHandler = null;
  let previouslyFocusedElement = null;

  function getFocusableElements(container) {
    if (!container) return [];

    return Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      const style = window.getComputedStyle(element);
      return !element.hasAttribute('hidden') &&
        style.display !== 'none' &&
        style.visibility !== 'hidden';
    });
  }

  function removeTrapFocus(elementToFocus = previouslyFocusedElement) {
    if (trappedContainer && trapKeydownHandler) {
      trappedContainer.removeEventListener('keydown', trapKeydownHandler);
    }

    trappedContainer = null;
    trapKeydownHandler = null;

    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      elementToFocus.focus();
    }
  }

  function trapFocus(container, elementToFocus = null) {
    if (!container) return;

    removeTrapFocus(null);
    trappedContainer = container;
    previouslyFocusedElement = document.activeElement;

    const focusableElements = getFocusableElements(container);
    const first = focusableElements[0] || container;
    const last = focusableElements[focusableElements.length - 1] || first;

    trapKeydownHandler = (event) => {
      if (event.key !== 'Tab') return;

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', trapKeydownHandler);

    const target = elementToFocus || first;
    if (target && typeof target.focus === 'function') target.focus();
  }

  function pauseAllMedia() {
    document.querySelectorAll('.js-youtube').forEach((video) => {
      video.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      );
    });

    document.querySelectorAll('.js-vimeo').forEach((video) => {
      video.contentWindow?.postMessage('{"method":"pause"}', '*');
    });

    document.querySelectorAll('video').forEach((video) => video.pause());

    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI?.pause) model.modelViewerUI.pause();
    });
  }

  function debounce(callback, wait) {
    let timeoutId;

    return function debounced(...args) {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback.apply(this, args), wait);
    };
  }

  function onKeyUpEscape(event) {
    if (event.code?.toUpperCase() !== 'ESCAPE') return;

    const openDetails = event.target?.closest?.('details[open]');
    if (!openDetails) return;

    const summary = openDetails.querySelector('summary');
    openDetails.removeAttribute('open');
    summary?.setAttribute('aria-expanded', 'false');
    summary?.focus();
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`
      }
    };
  }

  window.getFocusableElements = window.getFocusableElements || getFocusableElements;
  window.trapFocus = window.trapFocus || trapFocus;
  window.removeTrapFocus = window.removeTrapFocus || removeTrapFocus;
  window.pauseAllMedia = window.pauseAllMedia || pauseAllMedia;
  window.debounce = window.debounce || debounce;
  window.onKeyUpEscape = window.onKeyUpEscape || onKeyUpEscape;
  window.fetchConfig = window.fetchConfig || fetchConfig;

  console.warn(
    '[Shrine standalone] Remote bootstrap scripts were removed. Local compatibility helpers are active.'
  );
})();

/*
 * Standalone cart core
 * Restores the local ProductForm and CartDrawer behavior that was previously
 * supplied by the removed remote main.js bootstrap.
 */
(() => {
  'use strict';

  const CART_DRAWER_SECTION = 'cart-drawer';
  const CART_ICON_SECTION = 'cart-icon-bubble';

  function parseSection(html, selector) {
    if (!html) return null;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector(selector);
  }

  function getCartSections() {
    return [CART_DRAWER_SECTION, CART_ICON_SECTION];
  }

  class StandaloneCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;

      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('#CartDrawer-Overlay')) this.close();
      });

      this.bindHeaderCartLink();
    }

    bindHeaderCartLink() {
      const cartBubble = document.getElementById('cart-icon-bubble');
      const cartLink = cartBubble?.closest('a[href]') ||
        document.querySelector('a.header__icon--cart[href]');

      if (!cartLink || cartLink.dataset.standaloneDrawerBound === 'true') return;

      cartLink.dataset.standaloneDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
    }

    open(triggeredBy = null) {
      if (triggeredBy) this.activeElement = triggeredBy;

      this.classList.add('animate');
      document.body.classList.add('overflow-hidden');

      requestAnimationFrame(() => {
        this.classList.add('active');
        const inner = this.querySelector('.drawer__inner');
        if (inner && typeof window.trapFocus === 'function') {
          window.trapFocus(inner, inner.querySelector('.drawer__close') || inner);
        }
      });
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.activeElement);
      }

      window.setTimeout(() => this.classList.remove('animate'), 300);
    }

    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: CART_DRAWER_SECTION,
          selector: '#CartDrawer'
        },
        {
          id: 'cart-icon-bubble',
          section: CART_ICON_SECTION,
          selector: '.shopify-section'
        }
      ];
    }

    renderContents(cartData, shouldOpen = true) {
      if (!cartData?.sections) {
        console.error('[Shrine standalone] Cart response did not contain rendered sections.', cartData);
        return;
      }

      const drawerHtml = cartData.sections[CART_DRAWER_SECTION];
      const incomingDrawer = parseSection(drawerHtml, '#CartDrawer');
      const currentDrawer = this.querySelector('#CartDrawer');

      if (incomingDrawer && currentDrawer) {
        currentDrawer.innerHTML = incomingDrawer.innerHTML;
      }

      const iconHtml = cartData.sections[CART_ICON_SECTION];
      const incomingIconSection = parseSection(iconHtml, '.shopify-section');
      const currentIcon = document.getElementById('cart-icon-bubble');

      if (incomingIconSection && currentIcon) {
        currentIcon.innerHTML = incomingIconSection.innerHTML;
      }

      if (typeof cartData.item_count === 'number') {
        this.classList.toggle('is-empty', cartData.item_count === 0);
      } else {
        // /cart/add.js returns the added item rather than the complete cart.
        this.classList.remove('is-empty');
      }

      this.bindHeaderCartLink();
      if (shouldOpen) this.open(this.activeElement);
    }
  }

  class StandaloneProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.variantIdInput = this.form?.querySelector('[name="id"]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.querySelector('.product-form__error-message');
      this.isSubmitting = false;

      if (!this.form) return;
      if (this.variantIdInput) this.variantIdInput.disabled = false;

      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    get cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    clearError() {
      if (this.errorWrapper) this.errorWrapper.hidden = true;
      if (this.errorMessage) this.errorMessage.textContent = '';

      const inlineError = this.querySelector('.main-atc__error');
      if (inlineError) {
        inlineError.textContent = '';
        inlineError.style.display = 'none';
      }
    }

    showError(message) {
      const safeMessage = message || window.cartStrings?.error || 'Unable to add this product to the cart.';

      if (this.errorWrapper) this.errorWrapper.hidden = false;
      if (this.errorMessage) this.errorMessage.textContent = safeMessage;

      const inlineError = this.querySelector('.main-atc__error');
      if (inlineError) {
        inlineError.textContent = safeMessage;
        inlineError.style.display = 'block';
      }
    }

    setLoading(loading, activeButton = null) {
      const buttons = new Set([
        this.submitButton,
        activeButton?.closest?.('button') || activeButton
      ].filter(Boolean));

      buttons.forEach((button) => {
        button.toggleAttribute('aria-disabled', loading);
        button.classList.toggle('loading', loading);
        if (loading) button.setAttribute('disabled', 'disabled');
        else if (button.dataset.unavailable !== 'true') button.removeAttribute('disabled');

        const spinner = button.querySelector('.loading-overlay__spinner');
        spinner?.classList.toggle('hidden', !loading);
      });
    }

    isSkipCartRequest(event) {
      const eventButton = event?.submitter || event?.currentTarget || event?.target?.closest?.('button');
      return this.variantIdInput?.dataset.skipCart === 'true' ||
        eventButton?.dataset.skipCartButton === 'true';
    }

    async handleSubmit(event) {
      event?.preventDefault?.();
      if (!this.form || this.isSubmitting) return;
      if (typeof this.form.reportValidity === 'function' && !this.form.reportValidity()) return;

      const activeButton = event?.submitter || event?.currentTarget || this.submitButton;
      const skipCart = this.isSkipCartRequest(event);
      const drawer = this.cartDrawer;

      this.isSubmitting = true;
      this.clearError();
      this.setLoading(true, activeButton);

      try {
        const formData = new FormData(this.form);

        if (drawer) {
          formData.set('sections', getCartSections().join(','));
          formData.set('sections_url', window.location.pathname);
          drawer.activeElement = activeButton?.closest?.('button') || activeButton || this.submitButton;
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || window.cartStrings?.error);
        }

        if (skipCart) {
          window.location.assign('/checkout');
          return;
        }

        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(result, true);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        console.error('[Shrine standalone] Add to cart failed:', error);
        this.showError(error.message);
      } finally {
        this.isSubmitting = false;
        this.setLoading(false, activeButton);
      }
    }
  }

  class StandaloneQuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="number"]');
      if (!this.input) return;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[name="plus"], button[name="minus"]');
        if (!button) return;

        event.preventDefault();
        const previousValue = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        else this.input.stepDown();

        if (this.input.value !== previousValue) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  class StandaloneCartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        const line = Number(this.dataset.index || this.closest('[data-index]')?.dataset.index);
        const items = this.closest('cart-drawer-items');
        if (line && items?.updateQuantity) items.updateQuantity(line, 0);
      });
    }
  }

  class StandaloneCartDrawerItems extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', window.debounce((event) => {
        const input = event.target.closest('.quantity__input, input[name="updates[]"]');
        if (!input) return;

        const line = Number(input.dataset.index || input.closest('[data-index]')?.dataset.index);
        const quantity = Number(input.value);
        if (line && Number.isFinite(quantity)) this.updateQuantity(line, quantity);
      }, 250));
    }

    async updateQuantity(line, quantity) {
      this.classList.add('cart__items--disabled');

      try {
        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            line,
            quantity,
            sections: getCartSections(),
            sections_url: window.location.pathname
          })
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || window.cartStrings?.error);
        }

        const drawer = document.querySelector('cart-drawer');
        drawer?.renderContents?.(result, true);
      } catch (error) {
        console.error('[Shrine standalone] Cart update failed:', error);
        const errorContainer = document.getElementById('CartDrawer-CartErrors');
        if (errorContainer) errorContainer.textContent = error.message || window.cartStrings?.error || '';
      } finally {
        this.classList.remove('cart__items--disabled');
      }
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', StandaloneCartDrawer);
  }

  if (!customElements.get('product-form')) {
    customElements.define('product-form', StandaloneProductForm);
  }

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', StandaloneQuantityInput);
  }

  if (!customElements.get('cart-remove-button')) {
    customElements.define('cart-remove-button', StandaloneCartRemoveButton);
  }

  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', StandaloneCartDrawerItems);
  }
})();

/*
 * Standalone dynamic shipping dates
 * Restores the <dynamic-dates> custom element previously supplied by main.js.
 */
(() => {
  'use strict';

  const DEFAULT_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const DEFAULT_MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  const DAY_NAMES = [
    'sunday', 'monday', 'tuesday', 'wednesday',
    'thursday', 'friday', 'saturday'
  ];

  function splitLabels(value, fallback) {
    const labels = String(value || '')
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean);

    return labels.length === fallback.length ? labels : fallback;
  }

  function padNumber(value) {
    return String(value).padStart(2, '0');
  }

  function ordinalSuffix(value) {
    const number = Number(value);
    const lastTwo = number % 100;

    if (lastTwo >= 11 && lastTwo <= 13) return `${number}th`;

    switch (number % 10) {
      case 1:
        return `${number}st`;
      case 2:
        return `${number}nd`;
      case 3:
        return `${number}rd`;
      default:
        return `${number}th`;
    }
  }

  function normalizeSkippedDay(value) {
    const day = String(value || '').trim().toLowerCase();
    if (!day) return null;

    const exactIndex = DAY_NAMES.indexOf(day);
    if (exactIndex !== -1) return exactIndex;

    const prefixIndex = DAY_NAMES.findIndex((name) => name.startsWith(day.slice(0, 3)));
    return prefixIndex === -1 ? null : prefixIndex;
  }

  class StandaloneDynamicDates extends HTMLElement {
    connectedCallback() {
      this.renderDates();
    }

    get dayLabels() {
      return splitLabels(this.dataset.dayLabels, DEFAULT_DAY_LABELS);
    }

    get monthLabels() {
      return splitLabels(this.dataset.monthLabels, DEFAULT_MONTH_LABELS);
    }

    get skippedWeekDays() {
      return new Set(
        String(this.dataset.daysToSkip || '')
          .split(',')
          .map(normalizeSkippedDay)
          .filter((day) => day !== null)
      );
    }

    get skippedDates() {
      return new Set(
        String(this.dataset.datesToSkip || '')
          .split(',')
          .map((date) => date.trim())
          .filter((date) => /^\d{1,2}-\d{1,2}$/.test(date))
          .map((date) => {
            const [month, day] = date.split('-').map(Number);
            return `${padNumber(month)}-${padNumber(day)}`;
          })
      );
    }

    isSkipped(date) {
      const dateKey = `${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
      return this.skippedWeekDays.has(date.getDay()) || this.skippedDates.has(dateKey);
    }

    addRequiredDays(baseDate, requiredDays) {
      const result = new Date(baseDate.getTime());
      const requestedDays = Math.max(0, Number.parseInt(requiredDays, 10) || 0);
      let countedDays = 0;

      while (countedDays < requestedDays) {
        result.setDate(result.getDate() + 1);
        if (!this.isSkipped(result)) countedDays += 1;
      }

      return result;
    }

    formatDate(date) {
      const dayOfMonth = date.getDate();
      const numericDay = padNumber(dayOfMonth);
      const numericMonth = padNumber(date.getMonth() + 1);
      const monthLabel = this.monthLabels[date.getMonth()] || DEFAULT_MONTH_LABELS[date.getMonth()];

      // Day labels are configured Monday first, while Date#getDay() is Sunday first.
      const mondayFirstIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
      const dayLabel = this.dayLabels[mondayFirstIndex] || DEFAULT_DAY_LABELS[mondayFirstIndex];

      switch (this.dataset.dateFormat || 'mm_dd') {
        case 'day_mm_dd':
          return `${dayLabel}, ${monthLabel} ${ordinalSuffix(dayOfMonth)}`;
        case 'day_dd_mm':
          return `${dayLabel}, ${dayOfMonth}. ${monthLabel}`;
        case 'dd_mm':
          return `${dayOfMonth}. ${monthLabel}`;
        case 'dd_mm_no_dot':
          return `${dayOfMonth} ${monthLabel}`;
        case 'day_dd_mm_numeric':
          return `${dayLabel}, ${numericDay}. ${numericMonth}.`;
        case 'dd_mm_numeric':
          return `${numericDay}. ${numericMonth}.`;
        case 'mm_dd':
        default:
          return `${monthLabel} ${ordinalSuffix(dayOfMonth)}`;
      }
    }

    renderDates() {
      const today = new Date();
      today.setHours(12, 0, 0, 0);

      this.querySelectorAll('[data-dynamic-date="true"]').forEach((element) => {
        const template = element.dataset.text || element.innerHTML || '';
        const minimumDays = Number.parseInt(element.dataset.minDays, 10) || 0;
        const maximumDays = Number.parseInt(element.dataset.maxDays, 10);
        const normalizedMaximum = Number.isFinite(maximumDays) ? maximumDays : minimumDays;

        const startDate = this.formatDate(this.addRequiredDays(today, minimumDays));
        const endDate = this.formatDate(this.addRequiredDays(today, normalizedMaximum));

        element.innerHTML = template
          .replace(/\[start_date\]/gi, startDate)
          .replace(/\[end_date\]/gi, endDate);
      });
    }
  }

  if (!customElements.get('dynamic-dates')) {
    customElements.define('dynamic-dates', StandaloneDynamicDates);
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target
      ?.querySelectorAll?.('dynamic-dates')
      .forEach((element) => element.renderDates?.());
  });
})();

(() => {
  'use strict';

  class LocalSearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
    }

    connectedCallback() {
      if (!this.input || this.searchEvents) return;
      this.searchEvents = new AbortController();
      const options = { signal: this.searchEvents.signal };
      this.input.addEventListener('input', () => this.onChange(), options);
      this.input.form?.addEventListener('reset', (event) => this.onFormReset(event), options);
      this.toggleResetButton();
    }

    disconnectedCallback() {
      this.searchEvents?.abort();
      this.searchEvents = null;
    }

    onChange() { this.toggleResetButton(); }

    toggleResetButton() {
      this.resetButton?.classList.toggle('hidden', !this.input.value.length);
    }

    shouldResetForm() { return true; }

    onFormReset(event) {
      event.preventDefault();
      if (!this.shouldResetForm()) return;
      this.input.value = '';
      this.onChange();
      this.input.focus();
    }
  }

  window.SearchForm = window.SearchForm || LocalSearchForm;
  if (!customElements.get('search-form')) customElements.define('search-form', window.SearchForm);

  let instance = 0;
  class LocalPredictiveSearch extends window.SearchForm {
    constructor() {
      super();
      this.results = this.querySelector('[data-predictive-search]');
      this.status = this.querySelector('.predictive-search-status');
      this.instanceId = `local-predictive-${++instance}`;
      this.requestId = 0;
      this.isOpen = false;
    }

    connectedCallback() {
      super.connectedCallback();
      if (!this.input || !this.results || this.predictiveEvents) return;
      this.predictiveEvents = new AbortController();
      const options = { signal: this.predictiveEvents.signal };
      this.input.addEventListener('focus', () => this.onChange(), options);
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.addEventListener('focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        }, 0);
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close();
      }, options);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.predictiveEvents?.abort();
      this.predictiveEvents = null;
      clearTimeout(this.focusTimer);
      clearTimeout(this.statusTimer);
      this.close();
    }

    onChange() {
      super.onChange();
      this.close();
      const term = this.input.value.trim();
      if (!term) return;
      this.searchTimer = setTimeout(() => this.getSearchResults(term), 250);
    }

    async getSearchResults(term) {
      const requestId = ++this.requestId;
      this.requestController = new AbortController();
      this.setAttribute('loading', '');
      this.announce(this.dataset.loadingText || '');
      try {
        const route = window.routes?.predictive_search_url || `${window.Shopify?.routes?.root || '/'}search/suggest`;
        const url = new URL(route, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[limit]', '10');
        const response = await fetch(url, { signal: this.requestController.signal });
        if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = html.querySelector('#shopify-section-predictive-search');
        if (!section?.querySelector('#predictive-search-results')) throw new Error('Predictive search response is missing results');
        if (requestId !== this.requestId || !this.isConnected || this.input.value.trim() !== term) return;
        const ids = new Map();
        section.querySelectorAll('[id]').forEach((element) => {
          ids.set(element.id, `${this.instanceId}-${element.id}`);
          element.id = ids.get(element.id);
        });
        section.querySelectorAll('[aria-labelledby], [aria-controls]').forEach((element) => {
          ['aria-labelledby', 'aria-controls'].forEach((attribute) => {
            if (element.hasAttribute(attribute)) element.setAttribute(attribute,
              element.getAttribute(attribute).split(/\s+/).map((id) => ids.get(id) || id).join(' '));
          });
        });
        this.results.innerHTML = section.innerHTML;
        const listId = ids.get('predictive-search-results');
        this.input.setAttribute('aria-controls', listId);
        this.input.setAttribute('aria-owns', listId);
        this.setAttribute('results', 'true');
        this.open();
        this.announce(this.results.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
      } catch (error) {
        if (requestId === this.requestId) this.close();
      } finally {
        if (requestId === this.requestId) this.removeAttribute('loading');
      }
    }

    open() {
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.results.style.maxHeight = `${Math.max(120, window.innerHeight - this.input.getBoundingClientRect().bottom - 24)}px`;
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      clearTimeout(this.searchTimer);
      this.requestController?.abort();
      this.requestId++;
      this.removeAttribute('open');
      this.removeAttribute('loading');
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
      this.results?.querySelectorAll('[aria-selected="true"]').forEach((item) => item.setAttribute('aria-selected', 'false'));
      this.isOpen = false;
      if (clearSearchTerm && this.input) {
        this.input.value = '';
        this.toggleResetButton();
      }
    }

    announce(text) {
      if (!this.status) return;
      clearTimeout(this.statusTimer);
      this.status.textContent = text.trim();
      this.status.setAttribute('aria-hidden', 'false');
      this.statusTimer = setTimeout(() => this.status.setAttribute('aria-hidden', 'true'), 1000);
    }

    onKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        if (this.isOpen || this.hasAttribute('loading')) {
          event.preventDefault();
          event.stopPropagation();
        }
        this.close();
        return;
      }
      if (!this.isOpen) return;
      const items = Array.from(this.results.querySelectorAll('[role="option"]')).filter((item) => item.getClientRects().length);
      const selected = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      if (event.key === 'Enter' && selected >= 0) {
        event.preventDefault();
        const item = items[selected];
        (item.matches('a, button') ? item : item.querySelector('a, button'))?.click();
      } else if (items.length && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        const next = selected < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1) :
          (selected + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach((item, index) => item.setAttribute('aria-selected', String(index === next)));
        const item = items[next];
        if (!item.id) item.id = `${this.instanceId}-option-${next}`;
        this.input.setAttribute('aria-activedescendant', item.id);
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', LocalPredictiveSearch);
})();
